/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

// @react-jvectormap components
import { VectorMap } from "@react-jvectormap/core";
import { worldMerc } from "@react-jvectormap/world";

import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";

import { useNavigate } from "react-router";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import LinkIcon from "@mui/icons-material/Link";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SectionHeader from "components/patterns/SectionHeader";
import MDButton from "components/MDButton";
import HelpLabel from "components/HelpLabel";
import HelpTooltip from "components/HelpTooltip";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "components/layout/DashboardLayout";
import DashboardNavbar from "components/navigation/DashboardNavbar";
import SalesTable from "./components/SalesTable";
import DefaultLineChart from "./components/DefaultLineChart";
import PieChart from "./components/PieChart";
import DataTable from "components/patterns/DataTable";
import Footer from "components/layout/Footer";
import DefaultItem from "./components/DefaultItem";

import PropTypes from "prop-types";

import { googleMapKey } from "common/settings";
import { calculateServiceLife } from "common/utils";
import { useApiClient } from "common/ApiClientProvider";
import ROUTE_PATHS from "routePaths";

import SG from "assets/images/icons/flags/SG.png";
import JP from "assets/images/icons/flags/JP.png";

function GeoMap({ markers }) {
  return (
    <LoadScript googleMapsApiKey={googleMapKey}>
      <GoogleMap
        center={{ lat: 20, lng: 0 }}
        zoom={2}
        options={{ scrollwheel: false }}
        mapContainerStyle={{ height: `280px`, borderRadius: "6px", overflow: "hidden" }}
      >
        {markers}
      </GoogleMap>
    </LoadScript>
  );
}

GeoMap.propTypes = {
  markers: PropTypes.arrayOf(PropTypes.object).isRequired,
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Stack
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      mt={3}
      justifyContent="center"
      {...other}
    >
      {value === index && children}
    </Stack>
  );
}

TabPanel.defaultProps = {
  children: "",
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function Analysis() {
  const { t } = useTranslation("pages");
  const { t: tHelp } = useTranslation("help");
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const tableColumns = useMemo(
    () => [
      { Header: t("analysisTable.siteName"), accessor: "name" },
      { Header: t("analysisTable.currentPv"), accessor: "power" },
      {
        Header: (
          <HelpLabel helpText={tHelp("overview.problems")}>{t("analysisTable.problems")}</HelpLabel>
        ),
        accessor: "problems",
      },
      {
        Header: (
          <HelpLabel helpText={tHelp("overview.latestYearDegradation")}>
            {t("analysisTable.latestYearDegradation")}
          </HelpLabel>
        ),
        accessor: "latestYearDegradationRate",
      },
      {
        Header: (
          <HelpLabel helpText={tHelp("overview.degradationP95")}>
            {t("analysisTable.degradationP95")}
          </HelpLabel>
        ),
        accessor: "degradationExceedanceLevel",
      },
      {
        Header: (
          <HelpLabel helpText={tHelp("overview.degradationConfidence")}>
            {t("analysisTable.degradationConfidence")}
          </HelpLabel>
        ),
        accessor: "degradationConfidenceLevel",
      },
      {
        Header: (
          <HelpLabel helpText={tHelp("overview.degradationConfidenceInterval")}>
            {t("analysisTable.degradationConfidenceInterval")}
          </HelpLabel>
        ),
        accessor: "degradationConfidenceInterval",
      },
      {
        Header: (
          <HelpLabel helpText={tHelp("overview.cumulativeDegradation")}>
            {t("analysisTable.cumulativeDegradation")}
          </HelpLabel>
        ),
        accessor: "cumulativeDegradationRate",
      },
      {
        Header: (
          <HelpLabel helpText={tHelp("overview.p50SoilingRatio")}>
            {t("analysisTable.p50SoilingRatio")}
          </HelpLabel>
        ),
        accessor: "insolationWeightedSoilingRatio",
      },
      {
        Header: (
          <HelpLabel helpText={tHelp("overview.soilingConfidenceInterval")}>
            {t("analysisTable.soilingConfidenceInterval")}
          </HelpLabel>
        ),
        accessor: "confidenceIntervalSoilingRatio",
      },
      {
        Header: (
          <HelpLabel helpText={tHelp("overview.serviceLife")}>
            {t("analysisTable.serviceLife")}
          </HelpLabel>
        ),
        accessor: "serviceLife",
      },
      {
        Header: (
          <HelpLabel helpText={tHelp("overview.grade")}>{t("analysisTable.grade")}</HelpLabel>
        ),
        accessor: "grade",
      },
      {
        Header: (
          <HelpLabel helpText={tHelp("overview.category")}>{t("analysisTable.category")}</HelpLabel>
        ),
        accessor: "category",
      },
      {
        Header: (
          <HelpLabel helpText={tHelp("overview.powerGenerationForecast")}>
            {t("analysisTable.powerGenerationForecast")}
          </HelpLabel>
        ),
        accessor: "powerGenerationForecast",
      },
      {
        Header: (
          <HelpLabel helpText={tHelp("overview.environmentalValue")}>
            {t("analysisTable.environmentalValue")}
          </HelpLabel>
        ),
        accessor: "environmentalValue",
      },
      {
        Header: (
          <HelpLabel helpText={tHelp("overview.financialValue")}>
            {t("analysisTable.financialValue")}
          </HelpLabel>
        ),
        accessor: "financialValue",
      },
      {
        Header: (
          <HelpLabel helpText={tHelp("overview.actionMap")}>{t("analysisTable.action")}</HelpLabel>
        ),
        accessor: "actions",
      },
    ],
    [t, tHelp]
  );
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [processTime, setProcessTime] = useState("");
  const [status, setStatus] = useState("");
  const [mapTableDataProduction, setMapTableDataProduction] = useState([
    {
      country: ["", ""],
      red: "",
      yellow: "",
      green: "",
    },
  ]);
  const [timeLineDataProduction, setTimeLineDataProduction] = useState({});
  const [pieChartDataProduction, setPieChartDataProduction] = useState({
    labels: ["Red", "Yellow", "Green"],
    datasets: {
      label: "Breakdown",
      backgroundColors: ["error", "warning", "success"],
      data: [0, 0, 0],
    },
  });
  const [tableData, setTableData] = useState({
    columns: tableColumns,
    rows: [],
  });
  const [analysisSites, setAnalysisSites] = useState([]);
  const [exceedanceLevelMedian, setExceedanceLevelMedian] = useState(0);
  const [maxConfidenceLevel, setMaxConfidenceLevel] = useState(0);
  const [minConfidenceLevel, setMinConfidenceLevel] = useState(0);
  const [maxWeightedSoilingRatio, setMaxWeightedSoilingRatio] = useState(0);
  const [minWeightedSoilingRatio, setMinWeightedSoilingRatio] = useState(0);
  const [maxServiceLife, setMaxServiceLife] = useState(1999);
  const [minServiceLife, setMinServiceLife] = useState(2099);
  const [trendPowerGenerationDailyMax, setTrendPowerGenerationDailyMax] = useState("");
  const [trendPowerGenerationMonthlyMax, setTrendPowerGenerationMonthlyMax] = useState("");
  const [trendPowerGenerationDailyAverage, setTrendPowerGenerationDailyAverage] = useState("");
  const [trendPowerGenerationMonthlyAverage, setTrendPowerGenerationMonthlyAverage] = useState("");
  const [trendGraphData, setTrendGraphData] = useState({});
  const [markers, setMarkers] = useState([]);
  // const [yearArray, setYearArray] = useState([]);

  const renderProblems = (problems) => {
    const problemList = String(problems ?? "")
      .split(";")
      .map((problem) => problem.trim())
      .filter(Boolean);

    if (problemList.length === 0) {
      return "-";
    }

    return (
      <MDBox>
        {problemList.map((problem, index) => (
          <MDTypography key={index} variant="caption" display="block" color="text">
            {problem}
          </MDTypography>
        ))}
      </MDBox>
    );
  };

  const formatThreeDecimals = (value) => {
    const numberValue = Number(value);
    return Number.isNaN(numberValue) ? value : numberValue.toFixed(3);
  };

  const flags = {
    Singapore: SG,
    Japan: JP,
  };

  async function fetchData() {
    const [response, siteResponse] = await Promise.all([
      apiClient.get("get_trend_result", { query: { system_type: "ongrid" } }),
      apiClient.get("sites"),
    ]);
    const siteMap = new Map(siteResponse.map((site) => [String(site.id), site]));
    const analysisSites = response.sites.map((result) => ({
      ...result,
      ...siteMap.get(String(result.site)),
    }));
    const serviceLifeYears = analysisSites.map((prop) => {
      const installedYear = Number(prop.installed_date.split(" ")[0].split("-")[0]);
      return installedYear + prop.exceedance_levels.length + prop.service_life_rates.length;
    });
    setMinServiceLife(Math.min(...serviceLifeYears));
    setMaxServiceLife(Math.max(...serviceLifeYears));

    setAnalysisSites(
      analysisSites.map((site) => ({
        ...site,
      }))
    );
    const iconBase = "http://maps.google.com/mapfiles/ms/icons/";
    setMarkers(
      analysisSites.map((prop) => {
        const color = prop.status;
        const { coordinate } = prop;
        const [lat, lng] = coordinate.split(",");
        let iconUrl;
        if (color === "Red") {
          iconUrl = `${iconBase}red-dot.png`;
        } else if (color === "Yellow") {
          iconUrl = `${iconBase}yellow-dot.png`;
        } else {
          iconUrl = `${iconBase}green-dot.png`;
        }
        return (
          <Marker
            position={{ lat: Number(lat), lng: Number(lng) }}
            icon={iconUrl}
            key={prop.name}
          />
        );
      })
    );

    const median = (arr) => {
      const { length } = arr;

      arr.sort((a, b) => a - b);

      if (length % 2 === 0) {
        return (arr[length / 2 - 1] + arr[length / 2]) / 2;
      }

      return arr[(length - 1) / 2];
    };

    const exceedanceLavelArray = analysisSites.map((site) => site.exceedance_level);

    setExceedanceLevelMedian(median(exceedanceLavelArray).toFixed(3));

    const confidenceLevelArray = analysisSites.map((site) => site.confidence_level);

    setMaxConfidenceLevel(Math.max(...confidenceLevelArray).toFixed(3));
    setMinConfidenceLevel(Math.min(...confidenceLevelArray).toFixed(3));

    const weightedSoilingRatioArray = analysisSites.map(
      (site) => site.insolation_weighted_soiling_ratio
    );
    setMaxWeightedSoilingRatio(Math.max(...weightedSoilingRatioArray).toFixed(3));
    setMinWeightedSoilingRatio(Math.min(...weightedSoilingRatioArray).toFixed(3));

    const mapTableDataProductionList = response.analysis.production_statistics.map((prop) => ({
      country: [flags[prop[0]], prop[0]],
      red: prop[1],
      yellow: prop[2],
      green: prop[3],
    }));
    setMapTableDataProduction(mapTableDataProductionList);
    setTimeLineDataProduction({
      labels: response.analysis.trend_production_graph_data.labels.map((i) => i.split(" ")[0]),
      datasets: [
        {
          label: "Red Trend",
          color: "error",
          data: response.analysis.trend_production_graph_data.series[0],
        },
        {
          label: "Yellow Trend",
          color: "warning",
          data: response.analysis.trend_production_graph_data.series[1],
        },
        {
          label: "Green Trend",
          color: "success",
          data: response.analysis.trend_production_graph_data.series[2],
        },
      ],
    });
    setPieChartDataProduction({
      labels: ["Red", "Yellow", "Green"],
      datasets: {
        label: "Breakdown",
        backgroundColors: ["error", "warning", "success"],
        data: [
          response.analysis.production_statistics[0][1],
          response.analysis.production_statistics[0][2],
          response.analysis.production_statistics[0][3],
        ],
      },
    });
    const ongridResultResponse = await apiClient.get("ongrid_results/");
    setTrendPowerGenerationDailyMax(
      formatThreeDecimals(ongridResultResponse[0].overview.trend.daily_max)
    );
    setTrendPowerGenerationDailyAverage(
      formatThreeDecimals(ongridResultResponse[0].overview.trend.daily_mean)
    );
    setTrendPowerGenerationMonthlyMax(
      formatThreeDecimals(ongridResultResponse[0].overview.trend.monthly_max)
    );
    setTrendPowerGenerationMonthlyAverage(
      formatThreeDecimals(ongridResultResponse[0].overview.trend.monthly_mean)
    );
    setTrendGraphData({
      labels: ongridResultResponse[0].overview.trend.power_generation.label,
      datasets: [
        {
          label: "Power Generation",
          color: "success",
          data: ongridResultResponse[0].overview.trend.power_generation.series,
        },
      ],
    });
  }

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setTableData({
      columns: tableColumns,
      rows: analysisSites.map((prop, key) => ({
        id: key,
        name: prop.name,
        power: prop.panel_output,
        problems: renderProblems(prop.problems),
        /*
        latestYearDegradationRate: (
          <MDButton
            onClick={() => {
              navigate(ROUTE_PATHS.degradationSoiling, { state: { name: prop.name } });
            }}
          >
            {prop.exceedance_levels.slice(-1)[0][1].toFixed(3)}%/yr
          </MDButton>
        ),
        */
        latestYearDegradationRate: `${prop.exceedance_levels.slice(-1)[0][1].toFixed(3)}%/yr`,
        /*
        degradationExceedanceLevel: (
          <MDButton
            onClick={() => {
              navigate(ROUTE_PATHS.degradationSoiling, { state: { name: prop.name } });
            }}
          >
            {prop.exceedance_level.toFixed(3)}%/yr
          </MDButton>
        ),
        */
        degradationExceedanceLevel: `${prop.exceedance_level.toFixed(3)}%/yr`,
        /*
        degradationConfidenceLevel: (
          <MDButton
            onClick={() => {
              navigate(ROUTE_PATHS.degradationSoiling, { state: { name: prop.name } });
            }}
          >
            {prop.confidence_level.toFixed(3)}%/yr
          </MDButton>
        ),
        */
        degradationConfidenceLevel: `${prop.confidence_level.toFixed(3)}%/yr`,
        /*
        degradationConfidenceInterval: (
          <MDButton
            onClick={() => {
              navigate(ROUTE_PATHS.degradationSoiling, { state: { name: prop.name } });
            }}
          >
            {prop.confidence_interval[0].toFixed(3)} to {prop.confidence_interval[1].toFixed(3)}
          </MDButton>
        ),
        */
        degradationConfidenceInterval: `${prop.confidence_interval[0].toFixed(
          3
        )} to ${prop.confidence_interval[1].toFixed(3)}`,
        /*
        cumulativeDegradationRate: (
          <MDButton
            onClick={() => {
              navigate(ROUTE_PATHS.degradationSoiling, { state: { name: prop.name } });
            }}
          >
            {prop.exceedance_levels
              .map((i) => i[1])
              .reduce((r, i) => r + i, 0)
              .toFixed(3)}
            %
          </MDButton>
        ),
        */
        cumulativeDegradationRate: `${prop.exceedance_levels
          .map((i) => i[1])
          .reduce((r, i) => r + i, 0)
          .toFixed(3)} %`,
        /*
        insolationWeightedSoilingRatio: (
          <MDButton
            onClick={() => {
              navigate(ROUTE_PATHS.degradationSoiling, { state: { name: prop.name } });
            }}
          >
            {prop.insolation_weighted_soiling_ratio.toFixed(3)}
          </MDButton>
        ),
        */
        insolationWeightedSoilingRatio: prop.insolation_weighted_soiling_ratio.toFixed(3),
        /*
        confidenceIntervalSoilingRatio: (
          <MDButton
            onClick={() => {
              navigate(ROUTE_PATHS.degradationSoiling, { state: { name: prop.name } });
            }}
          >
            {prop.confidence_interval_soiling_ratio[0].toFixed(3)}-
            {prop.confidence_interval_soiling_ratio[1].toFixed(3)}
          </MDButton>
        ),
        */
        confidenceIntervalSoilingRatio: `${prop.confidence_interval_soiling_ratio[0].toFixed(
          3
        )}-${prop.confidence_interval_soiling_ratio[1].toFixed(3)}`,
        /*
        serviceLife: (
          <MDButton
            onClick={() => {
              navigate(ROUTE_PATHS.serviceLife, {
                state: {
                  prop,
                },
              });
            }}
          >
            {calculateServiceLife(prop)}
          </MDButton>
        ),
        */
        serviceLife: calculateServiceLife(prop),
        grade: prop.grade,
        category: prop.category,
        powerGenerationForecast: (
          <div className="actions-right">
            <MDButton
              onClick={() => {
                navigate(ROUTE_PATHS.powerGenerationForecast, {
                  state: {
                    prop,
                  },
                });
              }}
            >
              <Icon fontSize="medium" color="secondary">
                open_in_new
              </Icon>
            </MDButton>
          </div>
        ),
        environmentalValue: (
          <MDButton
            onClick={() => {
              navigate(ROUTE_PATHS.environmentalFinancial, {
                state: {
                  prop,
                },
              });
            }}
          >
            {prop.environmental_value.toFixed(3)}
            <LinkIcon fontSize="small" sx={{ ml: 0.5 }} />
          </MDButton>
        ),
        financialValue: (
          <MDButton
            onClick={() => {
              navigate(ROUTE_PATHS.environmentalFinancial, {
                state: {
                  prop,
                },
              });
            }}
          >
            {prop.financial_value.toFixed(3)}
            <LinkIcon fontSize="small" sx={{ ml: 0.5 }} />
          </MDButton>
        ),
        actions: (
          <div className="actions-right">
            <MDButton
              onClick={() => {
                const [lat, lng] = prop.coordinate.split(",");
                const markerArray = [];
                markerArray.push([prop.status, lat, lng]);
                navigate(ROUTE_PATHS.map, { state: { markerData: markerArray } });
              }}
            >
              <Icon fontSize="medium" color="primary">
                map
              </Icon>
            </MDButton>
          </div>
        ),
      })),
    });
  }, [analysisSites, tableColumns]);

  useEffect(() => {
    async function fetchTaskStatus() {
      const statusResponse = await apiClient.get("caculate_status");
      setStatus(statusResponse.task_status);
      setStartTime(statusResponse.start_time.split(".")[0]);
      setEndTime(statusResponse.end_time.split(".")[0]);
      setProcessTime(statusResponse.process_time);
    }
    fetchTaskStatus();
  }, []);

  const serviceLifeTrendGroups = [
    {
      title: t("analysisUi.trendDegradation"),
      metrics: [
        {
          title: t("analysisUi.trendMedianP95"),
          value: exceedanceLevelMedian,
          helpText: tHelp("overview.medianDegradation"),
        },
        {
          title: t("analysisUi.trendMaxConfidence"),
          value: maxConfidenceLevel,
          helpText: tHelp("overview.maximumDegradationConfidence"),
        },
        {
          title: t("analysisUi.trendMinConfidence"),
          value: minConfidenceLevel,
          helpText: tHelp("overview.minimumDegradationConfidence"),
        },
      ],
    },
    {
      title: t("analysisUi.trendSoiling"),
      metrics: [
        {
          title: t("analysisUi.trendMaxP50Ratio"),
          value: maxWeightedSoilingRatio,
          helpText: tHelp("overview.maximumSoilingRatio"),
        },
        {
          title: t("analysisUi.trendMinP50Ratio"),
          value: minWeightedSoilingRatio,
          helpText: tHelp("overview.minimumSoilingRatio"),
        },
      ],
    },
    {
      title: t("analysisUi.trendServiceLife"),
      metrics: [
        {
          title: t("analysisUi.trendMaxServiceLife"),
          value: maxServiceLife,
          helpText: tHelp("overview.maximumServiceLife"),
        },
        {
          title: t("analysisUi.trendMinServiceLife"),
          value: minServiceLife,
          helpText: tHelp("overview.minimumServiceLife"),
        },
      ],
    },
  ];

  const historicalAnalysisMetrics = [
    {
      title: t("analysisUi.dailyMax"),
      value: trendPowerGenerationDailyMax,
      helpText: tHelp("overview.powerDailyMax"),
    },
    {
      title: t("analysisUi.monthlyMax"),
      value: trendPowerGenerationMonthlyMax,
      helpText: tHelp("overview.powerMonthlyMax"),
    },
    {
      title: t("analysisUi.dailyAverage"),
      value: trendPowerGenerationDailyAverage,
      helpText: tHelp("overview.powerDailyAverage"),
    },
    {
      title: t("analysisUi.monthlyAverage"),
      value: trendPowerGenerationMonthlyAverage,
      helpText: tHelp("overview.powerMonthlyAverage"),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pb={3}>
        <br />
        <Grid container alignItems="center">
          <Grid item xs={12} md={12}>
            <Card sx={{ height: "100%", position: "relative" }}>
              <HelpTooltip
                title={tHelp("overview.latestTask")}
                sx={{ position: "absolute", top: 12, right: 12, zIndex: 2 }}
              />
              <MDBox pt={2} px={2} lineHeight={1}>
                <MDTypography variant="h6" fontWeight="medium">
                  {t("overview.latestTask")}
                </MDTypography>
              </MDBox>
              <MDBox p={2}>
                <DefaultItem
                  color="dark"
                  icon="tasks"
                  title={t("analysis.status")}
                  description={status}
                />
                <MDBox mt={2.5}>
                  <DefaultItem
                    color="dark"
                    icon="play_circle"
                    title={t("analysis.startTime")}
                    description={startTime}
                  />
                </MDBox>
                <MDBox mt={2.5}>
                  <DefaultItem
                    color="dark"
                    icon="stop_circle"
                    title={t("analysis.endTime")}
                    description={endTime}
                  />
                </MDBox>
                <MDBox mt={2.5}>
                  <DefaultItem
                    color="dark"
                    icon="notifications_active"
                    title={t("analysis.processTime")}
                    description={processTime}
                  />
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
        <Grid container alignItems="center">
          <Grid item xs={12} md={7}>
            <MDBox mt={3} mb={1}>
              <SectionHeader
                variant="page"
                title={t("analysis.serviceLifeTrend")}
                helpText={tHelp("overview.serviceLifeTrend")}
              />
            </MDBox>
          </Grid>
        </Grid>
        <MDBox mt={5}>
          <Grid container spacing={3}>
            {serviceLifeTrendGroups.map((group) => (
              <Grid item xs={12} key={group.title}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  {group.title}
                </MDTypography>
                <Grid container spacing={2}>
                  {group.metrics.map((metric) => (
                    <Grid item xs={12} md={12 / group.metrics.length} key={metric.title}>
                      <Card sx={{ height: "100%", position: "relative" }}>
                        <HelpTooltip
                          title={metric.helpText}
                          sx={{ position: "absolute", top: 12, right: 12, zIndex: 2 }}
                        />
                        <MDBox p={3}>
                          <MDTypography variant="body2" color="text" sx={{ whiteSpace: "nowrap" }}>
                            {metric.title}
                          </MDTypography>
                          <MDBox mt={2} mb={1} lineHeight={0}>
                            <MDTypography variant="h3" fontWeight="bold">
                              {metric.value}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            ))}
          </Grid>
        </MDBox>
        <MDBox mt={5}>
          <Card sx={{ width: "100%", position: "relative" }}>
            <HelpTooltip
              title={tHelp("overview.siteStatusTable")}
              sx={{ position: "absolute", top: 16, right: 16, zIndex: 2 }}
            />
            <MDBox display="flex">
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="4rem"
                height="4rem"
                variant="gradient"
                bgColor="success"
                color="white"
                shadow="md"
                borderRadius="xl"
                ml={3}
                mt={-2}
              >
                <Icon fontSize="medium" color="inherit">
                  assignment
                </Icon>
              </MDBox>
              <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
                {t("siteStatusTable")}
              </MDTypography>
            </MDBox>
            <MDBox p={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={12} md={12}>
                  <DataTable table={tableData} canSearch />
                </Grid>
              </Grid>
            </MDBox>
          </Card>
          <br />
        </MDBox>
        <Grid container alignItems="center">
          <Grid item xs={12} md={7}>
            <MDBox mt={3} mb={1}>
              <SectionHeader variant="page" title={t("analysisUi.systemStatusAllRegions")} />
            </MDBox>
          </Grid>
        </Grid>
        <MDBox mt={5} mb={1}>
          <Card sx={{ width: "100%", position: "relative" }}>
            <HelpTooltip
              title={tHelp("overview.systemStatus")}
              sx={{ position: "absolute", top: 16, right: 16, zIndex: 2 }}
            />
            <MDBox display="flex">
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="4rem"
                height="4rem"
                variant="gradient"
                bgColor="success"
                color="white"
                shadow="md"
                borderRadius="xl"
                ml={3}
                mt={-2}
              >
                <Icon fontSize="medium" color="inherit">
                  language
                </Icon>
              </MDBox>
              <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
                {t("analysisUi.systemStatus")}
              </MDTypography>
            </MDBox>
            <MDBox p={2}>
              <Grid container>
                <Grid item xs={12} md={7} lg={6}>
                  <SalesTable rows={mapTableDataProduction} shadow={false} />
                </Grid>
                <Grid item xs={12} md={5} lg={6} sx={{ mt: { xs: 5, lg: 0 } }}>
                  <VectorMap
                    map={worldMerc}
                    zoomOnScroll={false}
                    zoomButtons
                    focusOn={{
                      x: 0.5,
                      y: 0.5,
                      scale: 1,
                    }}
                    markersSelectable
                    backgroundColor="transparent"
                    markers={[
                      {
                        name: "Singapore",
                        latLng: [1.29027, 103.851959],
                      },
                    ]}
                    regionStyle={{
                      initial: {
                        fill: "#dee2e7",
                        "fill-opacity": 1,
                        stroke: "none",
                        "stroke-width": 0,
                        "stroke-opacity": 0,
                      },
                    }}
                    markerStyle={{
                      initial: {
                        fill: "#e91e63",
                        stroke: "#ffffff",
                        "stroke-width": 5,
                        "stroke-opacity": 0.5,
                        r: 7,
                      },
                      hover: {
                        fill: "E91E63",
                        stroke: "#ffffff",
                        "stroke-width": 5,
                        "stroke-opacity": 0.5,
                      },
                      selected: {
                        fill: "E91E63",
                        stroke: "#ffffff",
                        "stroke-width": 5,
                        "stroke-opacity": 0.5,
                      },
                    }}
                    style={{
                      marginTop: "-1.5rem",
                    }}
                    onRegionTipShow={() => false}
                    onMarkerTipShow={() => false}
                  />
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </MDBox>
        <Grid container alignItems="center">
          <Grid item xs={12} md={7}>
            <MDBox mt={3} mb={1}>
              <SectionHeader variant="page" title={t("analysisUi.systemStatusByCountry")} />
            </MDBox>
          </Grid>
        </Grid>
        <MDBox mt={5} mb={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={12} md={7}>
              <DefaultLineChart
                icon={{ component: "show_chart" }}
                title={t("analysisUi.timeSeries")}
                description="&nbsp;"
                chart={timeLineDataProduction}
                helpText={tHelp("overview.timeSeries")}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={5}>
              <PieChart
                icon={{ color: "success", component: "donut_small" }}
                title={t("analysisUi.pieChart")}
                description="Analytics Insights"
                chart={pieChartDataProduction}
                helpText={tHelp("overview.pieChart")}
              />
            </Grid>
          </Grid>
        </MDBox>
        <Grid container alignItems="center">
          <Grid item xs={12} md={7} mt={7}>
            <MDBox mb={1}>
              <SectionHeader
                variant="page"
                title={t("analysisUi.historicalAnalysis")}
                helpText={tHelp("overview.historicalAnalysis")}
              />
            </MDBox>
          </Grid>
        </Grid>
        <MDBox mt={5}>
          <MDTypography variant="h6" fontWeight="medium" mb={2}>
            {t("analysisUi.powerGeneration")}
          </MDTypography>
          <Grid container spacing={3}>
            {historicalAnalysisMetrics.map((metric) => (
              <Grid item xs={12} sm={6} md={3} key={metric.title}>
                <Card sx={{ height: "100%", position: "relative" }}>
                  <HelpTooltip
                    title={metric.helpText}
                    sx={{ position: "absolute", top: 12, right: 12, zIndex: 2 }}
                  />
                  <MDBox p={3}>
                    <MDTypography variant="body2" color="text" sx={{ whiteSpace: "nowrap" }}>
                      {metric.title}
                    </MDTypography>
                    <MDBox mt={2} mb={1} lineHeight={0}>
                      <MDTypography variant="h3" fontWeight="bold">
                        {metric.value}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            ))}
          </Grid>
          <br />
          <Grid container spacing={3}>
            <Grid item xs={12} md={12} lg={12}>
              <MDBox mb={1.5} mt={1.5}>
                <DefaultLineChart
                  icon={{ component: "show_chart" }}
                  title={t("analysisUi.trendGraph")}
                  description="&nbsp;"
                  chart={trendGraphData}
                  helpText={tHelp("overview.trendGraph")}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={5}>
          <Card sx={{ width: "100%", position: "relative" }}>
            <HelpTooltip
              title={tHelp("overview.statusMap")}
              sx={{ position: "absolute", top: 16, right: 16, zIndex: 2 }}
            />
            <MDBox display="flex">
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="4rem"
                height="4rem"
                variant="gradient"
                bgColor="success"
                color="white"
                shadow="md"
                borderRadius="xl"
                ml={3}
                mt={-2}
              >
                <Icon fontSize="medium" color="inherit">
                  map
                </Icon>
              </MDBox>
              <MDTypography variant="h6" sx={{ mt: 2, mb: 1, ml: 2 }}>
                {t("analysisUi.statusMap")}
              </MDTypography>
            </MDBox>
            <MDBox p={2}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={12} md={12}>
                  <GeoMap markers={markers} />
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Analysis;
